import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { appsAPI, appPlansAPI } from '../../services/api'
import SafeHTML from '../../components/SafeHTML'
import AppIcon from '../../components/public/AppIcon'
import { formatEuro, toNumber } from '../../utils/format'

import { logger } from '@/utils/logger';
export default function AppDetails() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const [app, setApp] = useState(null)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedBilling, setSelectedBilling] = useState('YEARLY') // YEARLY oder MONTHLY
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const closeLightbox = useCallback(() => setLightboxOpen(false), [])

  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeLightbox() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxOpen, closeLightbox])

  useEffect(() => {
    loadAppDetails()
  }, [slug])

  const loadAppDetails = async () => {
    try {
      // App über den Slug laden
      const appRes = await appsAPI.getBySlug(slug)
      setApp(appRes.data)

      // Tarife dieser App laden
      const plansRes = await appPlansAPI.getPublicPlans(appRes.data.id)
      setPlans(plansRes.data)
    } catch (err) {
      setError('Fehler beim Laden der App-Details')
      logger.error(err)
    } finally {
      setLoading(false)
    }
  }

  const parseFeatureList = (jsonString) => {
    try {
      return JSON.parse(jsonString || '[]')
    } catch {
      return []
    }
  }

  const handleOrderClick = (plan) => {
    // Mit Tarifdaten zur Kasse wechseln
    navigate(`/checkout`, {
      state: { app, plan, billingCycle: selectedBilling }
    })
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
        <span className="wc-spinner" role="status" aria-label="Lade App-Details" />
      </div>
    )
  }

  if (error || !app) {
    return (
      <div className="wc-container" style={{ padding: '120px 32px', textAlign: 'center' }}>
        <h2 style={{ marginBottom: 16 }}>App nicht gefunden</h2>
        <p className="wc-muted" style={{ marginBottom: 24 }}>
          {error || 'Diese App ist nicht mehr verfügbar.'}
        </p>
        <Link to="/apps" className="wc-btn wc-btn-primary">
          Zurück zur Übersicht
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Kopfbereich */}
      <section
        style={{
          borderBottom: '1px solid var(--wc-divider)',
          backgroundImage:
            'radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--wc-accent) 14%, transparent), transparent 55%)',
        }}
      >
        <div className="wc-container" style={{ padding: '56px 32px 48px' }}>
          <Link
            to="/apps"
            className="wc-btn wc-btn-ghost"
            style={{ marginBottom: 20, marginLeft: -4 }}
          >
            ← Zurück zur Übersicht
          </Link>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
            <AppIcon app={app} size={72} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0 }}>{app.name}</h1>
                {app.is_featured && <span className="wc-tag wc-tag-accent">Beliebt</span>}
              </div>
              {app.short_description && (
                <p style={{ fontSize: 17, opacity: 0.8, margin: '12px 0 0', maxWidth: 620 }}>
                  {app.short_description}
                </p>
              )}
              {toNumber(app.price_monthly) > 0 && (
                <p
                  className="wc-mono wc-muted"
                  style={{ fontSize: 13, margin: '14px 0 0' }}
                >
                  ab {formatEuro(app.price_monthly)} / Monat
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="wc-container" style={{ padding: '56px 32px' }}>
        {/* Beschreibung — Inhalt aus dem Backend */}
        {(app.description || app.image_url) && (
          <div className="wc-card wc-elev-sm" style={{ padding: 24, marginBottom: 56 }}>
            <h2 style={{ fontSize: 25, marginBottom: 16 }}>Über {app.name}</h2>

            {app.image_url && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                title="Vollbild anzeigen"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  cursor: 'zoom-in',
                  marginBottom: 20,
                }}
              >
                <img
                  src={app.image_url}
                  alt={app.name}
                  style={{
                    width: '100%',
                    maxHeight: 340,
                    objectFit: 'cover',
                    borderRadius: 'var(--wc-radius-md)',
                  }}
                />
              </button>
            )}

            {app.description && (
              <SafeHTML html={app.description} className="prose max-w-none" />
            )}
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && app.image_url && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${app.name} in Vollbild`}
            onClick={closeLightbox}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              display: 'grid',
              placeItems: 'center',
              padding: 16,
              background: 'color-mix(in srgb, #000 85%, transparent)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <button
              type="button"
              onClick={closeLightbox}
              aria-label="Schließen"
              className="wc-btn wc-btn-secondary wc-btn-icon"
              style={{ position: 'absolute', top: 16, right: 16 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            <img
              src={app.image_url}
              alt={app.name}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 'var(--wc-radius-lg)',
              }}
            />
          </div>
        )}

        {/* Tarife — Preise werden im Backend gepflegt */}
        {plans.length > 0 && (
          <div style={{ marginBottom: 56 }}>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <p className="wc-kicker">Tarife</p>
              <h2 style={{ marginBottom: 12 }}>Wählen Sie Ihren Plan</h2>
              <p className="wc-muted" style={{ marginBottom: 24 }}>
                Flexible Preismodelle für jeden Bedarf
              </p>

              <div className="wc-seg" role="group" aria-label="Abrechnungszeitraum">
                <button
                  type="button"
                  className="wc-seg-opt"
                  aria-pressed={selectedBilling === 'MONTHLY'}
                  onClick={() => setSelectedBilling('MONTHLY')}
                >
                  Monatlich
                </button>
                <button
                  type="button"
                  className="wc-seg-opt"
                  aria-pressed={selectedBilling === 'YEARLY'}
                  onClick={() => setSelectedBilling('YEARLY')}
                >
                  Jährlich
                </button>
              </div>
            </div>

            <div className="wc-grid-3" style={{ gap: 20, alignItems: 'stretch' }}>
              {plans.map(plan => {
                const monthly = toNumber(plan.price_monthly)
                const yearly = toNumber(plan.price_yearly)
                const price = selectedBilling === 'YEARLY' ? yearly : monthly
                const savings = monthly * 12 - yearly
                const savingsPercent =
                  monthly > 0 ? Math.round((savings / (monthly * 12)) * 100) : 0

                return (
                  <div
                    key={plan.id}
                    className="wc-card wc-card-hover"
                    style={{
                      padding: 24,
                      gap: 12,
                      boxShadow: plan.is_popular
                        ? '0 0 0 1px var(--wc-accent)'
                        : 'var(--wc-shadow-sm)',
                    }}
                  >
                    {/* Feste Höhe, damit die Tarifnamen auf einer Linie bleiben */}
                    <div style={{ minHeight: 23, display: 'flex' }}>
                      {plan.is_popular && (
                        <span className="wc-tag wc-tag-accent">Meistgewählt</span>
                      )}
                    </div>

                    <h3 style={{ marginBottom: 0 }}>{plan.name}</h3>
                    {plan.description && (
                      <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>
                        {plan.description}
                      </p>
                    )}

                    <div style={{ margin: '8px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 34, lineHeight: 1.1 }}>
                          {formatEuro(price)}
                        </span>
                        <span className="wc-muted" style={{ fontSize: 14 }}>
                          / {selectedBilling === 'YEARLY' ? 'Jahr' : 'Monat'}
                        </span>
                      </div>

                      {selectedBilling === 'YEARLY' && savings > 0 && (
                        <p style={{ color: 'var(--wc-accent-300)', fontSize: 13, margin: '6px 0 0' }}>
                          Sie sparen {formatEuro(savings)} ({savingsPercent} %) pro Jahr
                        </p>
                      )}
                      {selectedBilling === 'MONTHLY' && yearly > 0 && savings > 0 && (
                        <p className="wc-muted" style={{ fontSize: 13, margin: '6px 0 0' }}>
                          oder {formatEuro(yearly)} / Jahr ({savingsPercent} % günstiger)
                        </p>
                      )}
                    </div>

                    {plan.feature_list && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                          margin: '4px 0 8px',
                          flex: 1,
                        }}
                      >
                        {parseFeatureList(plan.feature_list).map((feature, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 10, fontSize: 14 }}>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="var(--wc-accent)"
                              strokeWidth="2"
                              style={{ flex: 'none', marginTop: 2 }}
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span style={{ opacity: 0.85 }}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOrderClick(plan)}
                      className={`wc-btn wc-btn-block ${
                        plan.is_popular ? 'wc-btn-primary' : 'wc-btn-secondary'
                      }`}
                      style={{ marginTop: 'auto' }}
                    >
                      Jetzt bestellen
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {plans.length === 0 && (
          <div
            className="wc-card wc-elev-sm"
            style={{ padding: 32, alignItems: 'center', marginBottom: 56 }}
          >
            <h3 style={{ marginBottom: 8 }}>Keine Preispläne verfügbar</h3>
            <p className="wc-muted" style={{ margin: 0 }}>
              Für diese App sind derzeit keine Preispläne konfiguriert.
            </p>
          </div>
        )}

        {/* Weiterführende Links */}
        {(app.demo_url || app.documentation_url) && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            {app.demo_url && (
              <a
                href={app.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="wc-btn wc-btn-secondary"
              >
                Demo ansehen
              </a>
            )}
            {app.documentation_url && (
              <a
                href={app.documentation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="wc-btn wc-btn-secondary"
              >
                Dokumentation
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
